/**
 * Ramadan coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_ramadan_eid`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const ramadanColoringPagesPack: ToilePack = {
    slug: 'ramadan-coloring-pages',
    category: 'season',
    name: 'Ramadan coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'mosque', label: 'Mosque', subject: 'a domed mosque with two minarets under a large crescent moon and stars, a row of arched windows and a garden path to the door'},
        {key: 'lantern', label: 'Lantern', subject: 'a tall Ramadan lantern hanging from a chain, its pierced metal panels in star and arch shapes, a crescent finial on top'},
        {key: 'iftar', label: 'Iftar table', subject: 'an iftar table seen from the front with a bowl of dates, a jug of water, glasses and a plate of samosas beside a small lantern'},
    ],
    styles: [
        {key: 'bold', label: 'Bold and simple', style: 'drawn for little ones in thick uniform outlines, few parts and big shapes', palette: 'black ink only', framing: 'the drawing centered on the page with a white margin'},
        {key: 'detailed', label: 'Detailed', style: 'drawn for older children in a thinner even line, with arches, tiles and small arabesque details to fill', palette: 'black ink only', framing: 'the drawing centered on the page with a white margin'},
        {key: 'border', label: 'Pattern border', style: 'drawn in a medium even line inside a wide border band of interlocking eight-pointed stars', palette: 'black ink only', framing: 'the scene and its border filling the page inside a white margin'},
    ],
    directions: ['a camel', 'moon sighting', 'for toddlers'],
    directionHint: 'try: a camel',
    seo: {
        title: 'Ramadan coloring pages: mosque, lantern, iftar table',
        metaDescription: 'Nine Ramadan coloring pages in three line ages: a domed mosque under the crescent, a hanging lantern and an iftar table with dates.',
        h1: 'Ramadan coloring pages: mosque, lantern, iftar table',
        tagline: 'Nine line drawings of three Ramadan scenes, each in a bold, a detailed and a pattern-border style.',
        intro: "Clean line drawings of the month's own things, to colour at home or in a weekend class while the household waits for iftar: a domed mosque under a crescent, a pierced metal lantern, a table with dates, water and samosas. The bold style suits a young child, the detailed style an older one, and the pattern border fills a whole page. Type a different subject, such as a camel, and the same line style draws it.",
        useCases: [
            'a quiet activity during the fasting afternoon',
            'a Sunday school or weekend class sheet',
            'a mosque kids corner table',
            'a page for a child waiting for iftar',
        ],
        faq: [
            {q: 'Which style is for which age?', a: 'Bold and simple uses thick, even outlines with few parts for little children. Detailed uses a thinner line with arches, tiles and arabesque details for older children.'},
            {q: 'Are the pictures ready to print?', a: 'Each is a black-ink line drawing on a white page with an even margin, ready to print and colour.'},
            {q: 'Can I ask for a different picture?', a: 'Yes. Type a subject such as a camel and it is drawn in the same line style. That is a request, not a promised result.'},
            {q: 'Is there any colour on the pages?', a: 'No. Every page is black ink only, with the shapes left empty to colour in.'},
        ],
    },
}
